import { handleError } from "@/api/handleError";
import { useAuthStore } from "@/stores/auth.store";
import { useBusinessStore } from "@/stores/businesses.store";
import { useUsersStore } from "@/stores/users.store";
import snakeCase from "lodash/snakeCase";
import { getIP, getPlatformDetails } from "../../platform";
import { userActivityEvents } from "../../queries";

export interface UserActivityEvents {
  identifier_type: string; // Unique identifier for the event (required)
  identifier_value?: string; // Identifies the object related to the event (required)
  event_time: string; // Timestamp of the event (required, ISO 8601 format)
  event_type?: string; // Type of event (default: "user_activity", optional)
  event_name: string; // Descriptive name of the event (required)
  properties?: Record<string, string | boolean>; // Custom properties for the event (optional, can be an empty object)
  device_type?: string; // Type of device triggering the event (optional)
  device_version?: string; // Version of the device (optional)
  user_agent?: string; // User agent string of the device (optional)
  source: string; // Source of the event (required)
  tags?: string[]; // Tags associated with the event (optional, can be an empty array)
  ip_address?: string | null;
}
export const sendUserActivityEvent = async (
  eventName: string,
  additionalProperties: Record<string, string | boolean | Date | undefined> = {}
) => {
  const { currentUserBusinessLevel, registerUser } = useUsersStore();
  const businessStore = useBusinessStore();
  const { isAuthenticated } = useAuthStore();

  const { osType, osVersion } = getPlatformDetails();
  const ipAddress = localStorage.getItem("ipAddress") ?? (await getIP());
  const currentDate = new Date();

  const convertKeysAndValuesToSnakeCase = (
    obj: Record<string, string | boolean | Date | undefined>
  ): Record<string, string | boolean> => {
    return Object.entries(obj).reduce(
      (acc, [key, value]) => {
        const snakeCaseKey = snakeCase(key);
        let snakeCaseValue: string | boolean;
        if (value instanceof Date) {
          snakeCaseValue = value.toISOString();
        } else if (typeof value === "string") {
          snakeCaseValue = snakeCase(value);
        } else {
          snakeCaseValue = value ?? false;
        }

        acc[snakeCaseKey] = snakeCaseValue;

        // Don't snake_case certain keys
        if (["email", "timestamp"].includes(snakeCaseKey)) {
          acc[snakeCaseKey] = value?.toString() ?? "";
        }

        return acc;
      },
      {} as Record<string, string | boolean>
    );
  };

  const properties: Record<string, string | boolean> = {
    ...convertKeysAndValuesToSnakeCase(additionalProperties),
  };

  //  if authenticated is true
  if (isAuthenticated) {
    if (businessStore.currentBusinessId) {
      properties.business_id = businessStore.currentBusinessId.toString();
      properties.user_role = businessStore.activeProfile?.profile.role || "";
    }

    if (currentUserBusinessLevel?.id) {
      properties.user_id = currentUserBusinessLevel?.id.toString();
    }
  }

  const emailIdentifier =
    businessStore.activeProfile?.profile?.email || registerUser?.personal_email;

  const event: UserActivityEvents = {
    identifier_type: emailIdentifier ? "email" : "ip_address",
    identifier_value: emailIdentifier || ipAddress || "",
    event_time: currentDate.toISOString(),
    event_type: "user_activity",
    event_name: eventName,
    device_version: osVersion,
    device_type: osType?.toLowerCase(),
    user_agent: navigator.userAgent,
    source: "web",
    ip_address: ipAddress,
    tags: [],
    properties,
  };
  try {
    await userActivityEvents(event);
  } catch (error) {
    handleError({ error });
  }
};
